import {
  trigger,
  state,
  style,
  transition,
  animate,
} from '@angular/animations';
import { NgClass, isPlatformBrowser } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  input,
  PLATFORM_ID,
  signal,
  ViewEncapsulation,
  inject,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { ProgressBarComponent } from '@edt/components/atoms/progress-bar/progress-bar.component';
import { Subscription, delay, interval, tap, timer } from 'rxjs';

@Component({
  selector: 'edt-announcement-bar',
  standalone: true,
  templateUrl: './announcement-bar.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgClass, MatButtonModule, MatIconModule, ProgressBarComponent],
  animations: [
    trigger('fadeAnimation', [
      state(
        'visible',
        style({
          opacity: 1,
        })
      ),
      state(
        'hidden',
        style({
          opacity: 0,
        })
      ),
      transition('visible => hidden', [animate('{{ fadeOutDuration }}')], {
        params: { fadeOutDuration: '0.5s' },
      }),
      transition('hidden => visible', [animate('{{ fadeInDuration }}')], {
        params: { fadeInDuration: '0.5s' },
      }),
    ]),
  ],
  styles: `
    edt-announcement-bar .transition-duration {
      transition-duration: var(--transition-duration);
    }
  `,
  encapsulation: ViewEncapsulation.None,
})
export class AnnouncementBarComponent {
  private platformId = inject(PLATFORM_ID);

  /** Listado de mensajes a mostrar */
  messages = input<string[]>([]);

  /** Duración de cada mensaje en pantalla. Unidad: ms */
  duration = input(5000);

  /** Duración de la animación para mostrar el mensaje: Unidad: ms */
  fadeInDuration = input(500);

  /** Duración de la animación para ocultar el mensaje. Unidad: ms */
  fadeOutDuration = input(500);

  /** Clases para personalizar el contenedor del componente */
  containerCss = input('');

  /** Clases para personalizar los mensajes */
  contentCss = input('');

  /** Clases para personalizar los botones de desplazamiento */
  actionButtonsCss = input('');

  /** Clases para personalizar la barra de progreso */
  barCss = input('');

  /** Clases para personalizar el progreso de la barra de progreso */
  progressCss = input('');

  /** Clases para perzonalizar los separadores de la barra de progreso */
  progressSeparatorsCss = input('');

  protected messageVisibility = signal<'hidden' | 'visible'>('visible');
  protected parsedFadeInDuration = computed(() => `${this.fadeInDuration()}ms`);
  protected parsedFadeOutDuration = computed(
    () => `${this.fadeOutDuration()}ms`
  );

  protected currentMessageIndex = signal<number>(-1);
  protected hasMessages = computed(() => this.messages().length > 0);
  protected currentMessage = computed(() =>
    this.hasMessages() ? this.messages()[this.currentMessageIndex()] : null
  );
  protected hasMultipleMessages = computed(() => this.messages().length > 1);

  private _messageInterval = computed(() =>
    interval(this.duration()).pipe(
      tap(() => this.messageVisibility.set('hidden')),
      delay(this.fadeOutDuration()),
      tap(() => {
        this.currentMessageIndex.set(this.nextMessageIndex);
        this.progressValue.set(this.duration() * this.currentMessageIndex());
        this.messageVisibility.set('visible');
      })
    )
  );
  private _messageIntervalSub: Subscription | null = null;

  protected progressValue = signal<number>(0);
  protected progressPorcent = computed(
    () =>
      (this.progressValue() * 100) /
      (this.duration() * this.messages().length - this.fadeOutDuration())
  );
  protected progressTransitionDuration = computed(() => {
    if (this.progressValue() === 0) return 0;
    else if (this.manualChangeSub()) return this.fadeOutDuration();
    else return 1000;
  });
  private _progressInterval = interval(1000).pipe(
    tap(() => this.progressValue.update(val => val + 1000))
  );
  private _progressIntervalSub: Subscription | null = null;

  protected manualChangeSub = signal<Subscription | null>(null);

  constructor() {
    effect(() => {
      this._clearIntervalSubscriptions();
      this.currentMessageIndex.set(0);
      this.progressValue.set(0);

      if (this.messages().length > 1 && isPlatformBrowser(this.platformId)) {
        this._setIntervalSubscriptions();
      }
    });
  }

  protected get nextMessageIndex() {
    const index = this.currentMessageIndex();

    if (index === -1) return -1;
    else if (index === this.messages().length - 1) return 0;
    else return index + 1;
  }

  protected get previousMessageIndex() {
    const index = this.currentMessageIndex();

    if (index === -1) return -1;
    else if (index === 0) return this.messages().length - 1;
    else return index - 1;
  }

  protected goToNextMessage() {
    this._clearIntervalSubscriptions();
    this.messageVisibility.set('hidden');

    this.manualChangeSub.set(
      timer(this.fadeOutDuration())
        .pipe(
          tap(() => {
            this.progressValue.set(this.duration() * this.nextMessageIndex);
            this.currentMessageIndex.set(this.nextMessageIndex);
            this.messageVisibility.set('visible');
          })
        )
        .subscribe(() => {
          this._setIntervalSubscriptions();
          this.manualChangeSub()?.unsubscribe();
          this.manualChangeSub.set(null);
        })
    );
  }

  protected goToPreviusMessage() {
    this._clearIntervalSubscriptions();
    this.messageVisibility.set('hidden');

    this.progressValue.set(this.duration() * this.previousMessageIndex);
    this.manualChangeSub.set(
      timer(this.fadeOutDuration())
        .pipe(
          tap(() => {
            this.currentMessageIndex.set(this.previousMessageIndex);
            this.messageVisibility.set('visible');
          })
        )
        .subscribe(() => {
          this._setIntervalSubscriptions();
          this.manualChangeSub()?.unsubscribe();
          this.manualChangeSub.set(null);
        })
    );
  }

  private _clearIntervalSubscriptions() {
    this._messageIntervalSub?.unsubscribe();
    this._messageIntervalSub = null;

    this._progressIntervalSub?.unsubscribe();
    this._progressIntervalSub = null;
  }

  private _setIntervalSubscriptions() {
    this._messageIntervalSub = this._messageInterval().subscribe();
    this._progressIntervalSub = this._progressInterval.subscribe();
  }
}
